/**
 * The editor's context menu (requirement 12.13).
 *
 * Built to the WAI-ARIA menu pattern so the right-click path has a keyboard
 * equivalent (D21): it opens with focus on the first enabled item, arrow keys
 * and Home/End move, Enter or Space activates, Escape or a click outside closes
 * and returns focus to where it came from.
 *
 * Commands only, with their shortcuts and separators: no headings and no
 * explanatory text. A command that does not apply is shown greyed.
 *
 * An item with `submenu` opens a second menu beside it, on hover or with the
 * right arrow, Enter or Space; the left arrow or Escape closes it and returns
 * to its item.
 */
import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

export interface MenuItem {
  label: string;
  shortcut?: string;
  onSelect?: () => void;
  /** When set, the item is shown disabled. The reason is not displayed. */
  disabledReason?: string;
  danger?: boolean;
  /** Choices shown in a menu beside this item rather than in this one. */
  submenu?: MenuItem[];
}

export type MenuEntry = MenuItem | "separator" | { heading: string };

function isItem(entry: MenuEntry): entry is MenuItem {
  return typeof entry === "object" && "label" in entry;
}

export function ContextMenu({
  x,
  y,
  entries,
  label,
  onClose,
  onBack,
  nested = false,
}: {
  x: number;
  y: number;
  entries: MenuEntry[];
  label: string;
  onClose: () => void;
  /** A submenu's way back to its parent item (left arrow, Escape). */
  onBack?: () => void;
  nested?: boolean;
}) {
  const [openSub, setOpenSub] = useState<{ index: number; x: number; y: number } | null>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const [position, setPosition] = useState({ x, y });
  const items = entries.filter(isItem);
  const enabled = items.map((item, index) => (item.disabledReason ? -1 : index)).filter((i) => i >= 0);
  const [active, setActive] = useState(enabled[0] ?? -1);

  // Keep the menu on screen: flip left or up if it would overflow.
  useLayoutEffect(() => {
    const menu = menuRef.current;
    if (!menu) return;
    const { width, height } = menu.getBoundingClientRect();
    setPosition({
      x: x + width > window.innerWidth - 8 ? Math.max(8, x - width) : x,
      y: y + height > window.innerHeight - 8 ? Math.max(8, y - height) : y,
    });
  }, [x, y]);

  useEffect(() => {
    // A submenu is closed by its parent's handlers, not by clicks of its own.
    if (nested) return;
    const previous = document.activeElement as HTMLElement | null;
    const close = (event: MouseEvent) => {
      const target = event.target as Element | null;
      if (!menuRef.current?.contains(target) && !target?.closest?.(".cx-menu")) onClose();
    };
    window.addEventListener("mousedown", close);
    window.addEventListener("blur", onClose);
    return () => {
      window.removeEventListener("mousedown", close);
      window.removeEventListener("blur", onClose);
      previous?.focus?.();
    };
  }, [onClose, nested]);

  useEffect(() => {
    menuRef.current?.querySelector<HTMLElement>(`[data-index="${active}"]`)?.focus();
  }, [active]);

  const move = (step: number) => {
    if (enabled.length === 0) return;
    const at = enabled.indexOf(active);
    setActive(enabled[(at + step + enabled.length) % enabled.length]!);
  };

  // A submenu takes focus when it mounts (its first item), so opening one
  // from the keyboard lands in it; from the pointer, hovering its items does.
  const openSubmenu = (index: number) => {
    const element = menuRef.current?.querySelector<HTMLElement>(`[data-index="${index}"]`);
    const box = element?.getBoundingClientRect();
    setOpenSub({ index, x: box ? box.right - 2 : position.x, y: box ? box.top - 4 : position.y });
  };

  const activate = (item: MenuItem, index: number) => {
    if (item.disabledReason) return;
    if (item.submenu) {
      openSubmenu(index);
      return;
    }
    onClose();
    item.onSelect?.();
  };

  const menu = (
    <div
      ref={menuRef}
      className="cx-menu"
      role="menu"
      aria-label={label}
      style={{ left: position.x, top: position.y }}
      onContextMenu={(event) => event.preventDefault()}
      onKeyDown={(event) => {
        // Keys inside an open submenu are its own; they bubble here too.
        if ((event.target as Element).closest?.(".cx-menu") !== menuRef.current) return;
        const current = items[active];
        const keys: Record<string, () => void> = {
          ArrowDown: () => move(1),
          ArrowUp: () => move(-1),
          Home: () => setActive(enabled[0] ?? -1),
          End: () => setActive(enabled[enabled.length - 1] ?? -1),
          Escape: onBack ?? onClose,
          Tab: onClose,
          ...(onBack ? { ArrowLeft: onBack } : {}),
          ...(current?.submenu && !current.disabledReason ? { ArrowRight: () => openSubmenu(active) } : {}),
        };
        const handler = keys[event.key];
        if (handler) {
          event.preventDefault();
          handler();
        }
      }}
    >
      {entries.map((entry, position) => {
        if (entry === "separator") {
          // No separator at the top, or two in a row once headings are gone.
          const previous = entries.slice(0, position).filter((e) => typeof e === "string" || isItem(e));
          if (previous.length === 0 || previous[previous.length - 1] === "separator") return null;
          return <div key={`sep-${position}`} role="separator" className="cx-menu-sep" />;
        }
        if (!isItem(entry)) {
          return null;
        }
        const itemIndex = items.indexOf(entry);
        return (
          <div
            key={entry.label}
            role="menuitem"
            tabIndex={-1}
            data-index={itemIndex}
            aria-disabled={entry.disabledReason ? true : undefined}
            className={`cx-menu-item${entry.danger ? " cx-menu-danger" : ""}`}
            aria-haspopup={entry.submenu ? "menu" : undefined}
            aria-expanded={entry.submenu ? openSub?.index === itemIndex : undefined}
            onMouseEnter={() => {
              if (entry.disabledReason) return;
              setActive(itemIndex);
              if (entry.submenu) openSubmenu(itemIndex);
              else setOpenSub(null);
            }}
            onClick={() => activate(entry, itemIndex)}
            onKeyDown={(event) => {
              if (event.key === "Enter" || event.key === " ") {
                event.preventDefault();
                activate(entry, itemIndex);
              }
            }}
          >
            <span>{entry.label}</span>
            {entry.shortcut && <kbd>{entry.shortcut}</kbd>}
            {entry.submenu && <span className="cx-menu-more" aria-hidden="true">›</span>}
          </div>
        );
      })}
      {openSub && items[openSub.index]?.submenu && (
        <ContextMenu
          nested
          x={openSub.x}
          y={openSub.y}
          label={items[openSub.index]!.label}
          entries={items[openSub.index]!.submenu!}
          onClose={onClose}
          onBack={() => {
            setOpenSub(null);
            menuRef.current?.querySelector<HTMLElement>(`[data-index="${openSub.index}"]`)?.focus();
          }}
        />
      )}
    </div>
  );
  // At the document's root: `x` and `y` are viewport coordinates, and a
  // transformed ancestor (the floating view palette is centred with one)
  // would otherwise become the containing block for `position: fixed` and
  // shift the menu by its own offset. A submenu sits inside its parent menu,
  // which is already at the root. React events still bubble through the
  // component tree, so keyboard handling is unchanged.
  return nested ? menu : createPortal(menu, document.body);
}
